import type { ReactNode } from "react";
import { PLATFORM_META, type SgPlatform } from "../../../features/sg/platforms";
import { PlatformLogo, platformLabel } from "./PlatformLogo";

function statusClasses(label: string, isDarkMode: boolean): string {
  const l = (label || "").toLowerCase();
  const green = isDarkMode
    ? "bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30"
    : "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200";
  const red = isDarkMode
    ? "bg-red-500/15 text-red-300 ring-1 ring-red-500/30"
    : "bg-red-50 text-red-700 ring-1 ring-red-200";
  const amber = isDarkMode
    ? "bg-amber-500/15 text-amber-300 ring-1 ring-amber-500/30"
    : "bg-amber-50 text-amber-700 ring-1 ring-amber-200";
  const gray = isDarkMode
    ? "bg-white/10 text-gray-300 ring-1 ring-white/15"
    : "bg-gray-100 text-gray-600 ring-1 ring-gray-200";

  if (l.includes("no factur") || l.includes("termin") || l.includes("suspend") || l.includes("baja"))
    return red;
  if (l.includes("factur")) return green;
  if (l.includes("inactiv") || l.includes("sin ")) return amber;
  if (l.includes("activ") || l.includes("disponible")) return green;
  return gray;
}

export function StatusBadge({
  label,
  isDarkMode,
}: {
  label: string;
  isDarkMode: boolean;
}): ReactNode {
  if (!label || label === "—")
    return <span className="text-gray-400">—</span>;
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusClasses(
        label,
        isDarkMode
      )}`}
    >
      {label}
    </span>
  );
}

export function OriginChips({ origins }: { origins: string[] }): ReactNode {
  if (!origins?.length) return <span className="text-gray-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {origins.map((o) => (
        <span
          key={o}
          title={platformLabel(o)}
          className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium ring-1"
          style={{
            color: PLATFORM_META[o as SgPlatform]?.color,
            background: `${PLATFORM_META[o as SgPlatform]?.color ?? "#9CA3AF"}1F`,
            ["--tw-ring-color" as string]: `${PLATFORM_META[o as SgPlatform]?.color ?? "#9CA3AF"}4D`,
          }}
        >
          {o in PLATFORM_META && <PlatformLogo platform={o as SgPlatform} size={12} />}
          {platformLabel(o)}
        </span>
      ))}
    </div>
  );
}

export function UserCell({
  name,
  isDarkMode,
}: {
  name: string;
  isDarkMode: boolean;
}): ReactNode {
  const initial = name?.charAt(0)?.toUpperCase() || "?";
  return (
    <div className="flex items-center gap-2.5">
      <div
        className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold ${
          isDarkMode
            ? "bg-primary-500/20 text-primary-300"
            : "bg-primary-500/10 text-primary-600"
        }`}
      >
        {initial}
      </div>
      <span className="truncate">{name || "—"}</span>
    </div>
  );
}

const DOT_COLOR: Record<string, string> = {
  green: "bg-emerald-500",
  yellow: "bg-amber-500",
  red: "bg-red-500",
};

export function ConnCell({
  status,
  label,
  live,
}: {
  status: "green" | "yellow" | "red" | null;
  label: string;
  live?: boolean;
}): ReactNode {
  return (
    <span className="inline-flex items-center gap-2">
      <span
        className={`inline-block w-2 h-2 rounded-full ${
          status ? DOT_COLOR[status] : "bg-gray-400"
        } ${live ? "ring-2 ring-offset-1 ring-emerald-400/40" : ""}`}
      />
      <span>{label}</span>
    </span>
  );
}

export function NumCell({
  value,
  highlight,
  isDarkMode,
}: {
  value: number;
  highlight?: boolean;
  isDarkMode: boolean;
}): ReactNode {
  if (!highlight) return <span>{value}</span>;
  return (
    <span
      className={`font-semibold ${
        isDarkMode ? "text-primary-300" : "text-primary-600"
      }`}
    >
      {value}
    </span>
  );
}
